import * as React from "react";
import {
  Field,
  Input,
  Checkbox,
  Button,
  tokens,
  makeStyles,
} from "@fluentui/react-components";
import { Add16Regular, Delete16Regular } from "@fluentui/react-icons";
import { TicketType } from "../types/glpi";
import { genericTaskLabel } from "../types/glpiConstants";

export interface GenericTaskItem {
  id: string;
  label?: string;
  /** Corps GLPI de la tâche (HTML ou texte). Si absent, le libellé est utilisé. */
  content?: string;
  date: string; // YYYY-MM-DD (input HTML natif)
  durationMin: string; // string brut pour l'input numérique
}

export interface GenericTaskValue {
  enabled: boolean;
  label: string;
  tasks: GenericTaskItem[];
}

interface Props {
  ticketType: TicketType;
  value: GenericTaskValue;
  onChange: (v: GenericTaskValue) => void;
  /** Si false : pas de case « Créer une tâche », la liste est toujours visible (ajout à ticket existant). */
  showEnableToggle?: boolean;
  /** Titre au-dessus du bloc (optionnel). */
  sectionTitle?: string;
}

const useStyles = makeStyles({
  root: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  taskCard: {
    padding: "8px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  row2: {
    display: "flex",
    gap: "8px",
    alignItems: "end",
  },
  dateCol: {
    flex: 1,
    minWidth: "140px",
  },
  durationCol: {
    width: "110px",
  },
  actionsCol: {
    display: "flex",
    alignItems: "center",
    marginLeft: "auto",
  },
});

export function defaultTaskValue(ticketType: TicketType): GenericTaskValue {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  const defaultLabel = genericTaskLabel(ticketType);
  return {
    enabled: true,
    label: defaultLabel,
    tasks: [
      {
        id: `${Date.now()}`,
        label: defaultLabel,
        date: `${yyyy}-${mm}-${dd}`,
        durationMin: "",
      },
    ],
  };
}

export function GenericTaskBlock({
  ticketType,
  value,
  onChange,
  showEnableToggle = true,
  sectionTitle,
}: Props): React.ReactElement {
  const styles = useStyles();
  const showTasks = showEnableToggle ? value.enabled : true;

  // Si le type change, on resynchronise le libellé par défaut sauf s'il a été modifié.
  React.useEffect(() => {
    const incidentLabel = genericTaskLabel(1);
    const requestLabel = genericTaskLabel(2);
    const nextDefault = genericTaskLabel(ticketType);
    if (value.label === incidentLabel || value.label === requestLabel) {
      // On garde un "default label" interne pour pré-remplir les nouvelles lignes.
      // Si des lignes n'ont pas d'intitulé explicite, on les recale aussi.
      onChange({
        ...value,
        label: nextDefault,
        tasks: (value.tasks || []).map((t) => {
          if (!t.label || t.label === incidentLabel || t.label === requestLabel) {
            return { ...t, label: nextDefault };
          }
          return t;
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketType]);

  function addTaskRow(): void {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const next: GenericTaskItem = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      label: value.label || genericTaskLabel(ticketType),
      date: `${yyyy}-${mm}-${dd}`,
      durationMin: "",
    };
    onChange({ ...value, tasks: [...(value.tasks || []), next] });
  }

  function removeTaskRow(id: string): void {
    const remaining = (value.tasks || []).filter((t) => t.id !== id);
    onChange({ ...value, tasks: remaining.length > 0 ? remaining : [] });
  }

  function updateTaskRow(id: string, patch: Partial<GenericTaskItem>): void {
    onChange({
      ...value,
      tasks: (value.tasks || []).map((t) => (t.id === id ? { ...t, ...patch } : t)),
    });
  }

  return (
    <div className={styles.root}>
      {showEnableToggle ? (
        <Checkbox
          checked={value.enabled}
          onChange={(_, d) => onChange({ ...value, enabled: !!d.checked })}
          label="Créer une tâche générique"
        />
      ) : (
        sectionTitle ? (
          <strong style={{ fontSize: "14px" }}>{sectionTitle}</strong>
        ) : null
      )}

      {showTasks && (
        <>
          {(value.tasks || []).map((t, idx) => (
            <div key={t.id} className={styles.taskCard}>
              <Field label={idx === 0 ? "Intitulé" : undefined}>
                <Input
                  value={t.label ?? value.label}
                  onChange={(_, d) => updateTaskRow(t.id, { label: d.value })}
                  placeholder={value.label}
                />
              </Field>

              <div className={styles.row2}>
                <Field label={idx === 0 ? "Date" : undefined} className={styles.dateCol}>
                  <Input
                    type="date"
                    value={t.date}
                    onChange={(_, d) => updateTaskRow(t.id, { date: d.value })}
                  />
                </Field>
                <Field
                  label={idx === 0 ? "Durée (min)" : undefined}
                  className={styles.durationCol}
                >
                  <Input
                    type="number"
                    min={0}
                    value={t.durationMin}
                    onChange={(_, d) => updateTaskRow(t.id, { durationMin: d.value })}
                    placeholder="0"
                  />
                </Field>
                <div className={styles.actionsCol}>
                  <Button
                    appearance="subtle"
                    icon={<Delete16Regular />}
                    aria-label="Supprimer cette tâche"
                    onClick={() => removeTaskRow(t.id)}
                    disabled={(value.tasks || []).length <= 1}
                  />
                </div>
              </div>
            </div>
          ))}

          <Button appearance="secondary" icon={<Add16Regular />} onClick={addTaskRow}>
            Ajouter une tâche
          </Button>
        </>
      )}
    </div>
  );
}
